import { useState } from "react";
import { Button, setPromiseSnack } from "@synthesis/shared-ui";
import { detailsQueryKey, useSummary } from "@synthesis/shared-services/internal/monitoring/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useMonitoringExport } from "./hooks/use-monitoring-export";
import { useClientType } from "../../shared/hooks/use-client-type";
import { useUserInfo } from "../../shared/hooks/use-user-info";
import { useUserRoles } from "../../shared/hooks/use-user-roles";

import { ActiveApplicationsSection } from "./local-components/active-applications-section";
import { MonitoringPageHeader } from "./local-components/monitoring-page-header";
import { RailwaysSection } from "./local-components/railways-section";
import { SystemStateSection } from "./local-components/system-state-section";
import { ALL_RAILWAYS_VALUE, getMonitoringRailwayQuery, resolveMonitoringRailway, type RailwayScope } from "./monitoring-page.lib";

const MonitoringPage = () => {
  const [selectedRailwayId, setRailwayId] = useState(ALL_RAILWAYS_VALUE);
  const [railwayScope, setRailwayScope] = useState<RailwayScope>("active");
  const clientType = useClientType();
  const roles = useUserRoles();
  const userInfo = useUserInfo();
  const isRailwayRestricted = clientType === "internal" && (roles.isInternalNtpChief || roles.isInternalApparatusNg);
  const userRailway = resolveMonitoringRailway(userInfo?.railwayCandidates ?? []);
  const railwayId = isRailwayRestricted ? (userRailway?.id ?? "") : selectedRailwayId;
  const canLoad = clientType === "internal" && (!isRailwayRestricted || !!userRailway);
  const queryClient = useQueryClient();
  const summary = useSummary({ query: { railway: getMonitoringRailwayQuery(railwayId) } }, { query: { enabled: canLoad } });
  const exportMutation = useMonitoringExport(railwayId);
  const refresh = async () => {
    await Promise.all([summary.refetch(), queryClient.invalidateQueries({ queryKey: detailsQueryKey({ query: { metric: "TOTAL" } }).slice(0, 1) })]);
  };

  return (
    <main className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto px-8 pb-8 pt-6">
      <div className="mx-auto flex w-full max-w-[76rem] flex-col gap-8">
        <MonitoringPageHeader
          updatedAt={summary.data?.calculatedAt}
          isUpdating={summary.isFetching}
          isExporting={exportMutation.isPending}
          isDisabled={!canLoad}
          railwayId={railwayId}
          isRailwayRestricted={isRailwayRestricted}
          railwayName={isRailwayRestricted ? userRailway?.name : undefined}
          onRailwayChange={setRailwayId}
          onRefresh={() => void refresh()}
          onExport={() => {
            void setPromiseSnack({
              promise: exportMutation.mutateAsync(),
              promiseSettings: {
                title: {
                  pending: "Экспорт мониторинга",
                  fulfilled: "Файл мониторинга скачан",
                  rejected: "Не удалось экспортировать мониторинг",
                },
              },
            });
          }}
        />
        {isRailwayRestricted && !userRailway ? (
          <p role="alert" className="paragraph-base text-type-error">
            Не удалось определить закреплённую за вами дорогу. Обратитесь к администратору.
          </p>
        ) : summary.isPending ? (
          <p role="status" className="paragraph-base text-type-minor">
            Загрузка мониторинга…
          </p>
        ) : summary.isError ? (
          <div role="alert" className="flex items-center gap-4">
            <p className="paragraph-base text-type-error">Не удалось загрузить мониторинг</p>
            <Button type="button" theme="secondary" size="sm" onClick={() => void summary.refetch()}>
              Повторить
            </Button>
          </div>
        ) : summary.data ? (
          <>
            <SystemStateSection railwayId={railwayId} data={summary.data} />
            <ActiveApplicationsSection railwayId={railwayId} data={summary.data} />
            {!isRailwayRestricted && (
              <RailwaysSection data={summary.data} railwayId={railwayId} railwayScope={railwayScope} onRailwayScopeChange={setRailwayScope} />
            )}
          </>
        ) : null}
      </div>
    </main>
  );
};

export default MonitoringPage;
